'use client';

import React, { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react';
import UnsavedWarningModal from '@/app/components/common/UnsavedWarningModal';
import { useRouter } from 'next/navigation';
import {
  registerGlobalShowWarning,
  clearGlobalUnsavedState,
} from '@/app/hooks/use-navigation-guard';

interface PendingNavigation {
  type: 'push' | 'replace' | 'back' | 'link';
  url?: string;
  options?: any;
}

interface NavigationGuardContextType {
  isModalOpen: boolean;
  pendingNavigation: PendingNavigation | null;
  setModalOpen: (open: boolean) => void;
  setPendingNavigation: (nav: PendingNavigation | null) => void;
  showNavigationWarning: (navigation: PendingNavigation) => void;
  executePendingNavigation: () => void;
  setHandlers: (onDiscard?: () => void, onSave?: () => void) => void;
}

const NavigationGuardContext = createContext<NavigationGuardContextType | undefined>(undefined);

export const NavigationGuardProvider = ({ children }: { children: React.ReactNode }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState<PendingNavigation | null>(null);
  const router = useRouter();

  const onDiscardHandlerRef = useRef<(() => void) | undefined>(undefined);
  const onSaveHandlerRef = useRef<(() => void) | undefined>(undefined);

  const executePendingNavigation = useCallback(() => {
    if (!pendingNavigation) return;

    const { type, url, options } = pendingNavigation;

    clearGlobalUnsavedState();

    if (type === 'push' && url) {
      router.push(url, options);
    } else if (type === 'replace' && url) {
      router.replace(url, options);
    } else if (type === 'back') {
      router.back();
    } else if (type === 'link' && url) {
      router.push(url);
    }

    setPendingNavigation(null);
  }, [pendingNavigation, router]);

  const showNavigationWarning = useCallback((navigation: PendingNavigation) => {
    setPendingNavigation(navigation);
    setIsModalOpen(true);
  }, []);

  useEffect(() => {
    registerGlobalShowWarning(showNavigationWarning);
  }, [showNavigationWarning]);

  const setHandlers = useCallback((onDiscard?: () => void, onSave?: () => void) => {
    onDiscardHandlerRef.current = onDiscard;
    onSaveHandlerRef.current = onSave;
  }, []);

  const handleDiscard = useCallback(() => {
    setIsModalOpen(false);
    const handler = onDiscardHandlerRef.current;
    if (handler) {
      handler();
    } else {
      executePendingNavigation();
    }
  }, [executePendingNavigation]);

  const handleSave = useCallback(() => {
    setIsModalOpen(false);
    const handler = onSaveHandlerRef.current;
    if (handler) {
      handler();
    }
  }, []);

  return (
    <NavigationGuardContext.Provider
      value={{
        isModalOpen,
        pendingNavigation,
        setModalOpen: setIsModalOpen,
        setPendingNavigation,
        showNavigationWarning,
        executePendingNavigation,
        setHandlers,
      }}
    >
      {children}
      <UnsavedWarningModal
        isOpen={isModalOpen}
        onOpenChange={setIsModalOpen}
        onDiscard={handleDiscard}
        onSave={handleSave}
      />
    </NavigationGuardContext.Provider>
  );
};

export const useNavigationGuardContext = () => {
  const context = useContext(NavigationGuardContext);
  if (context === undefined) {
    throw new Error('useNavigationGuardContext must be used within a NavigationGuardProvider');
  }
  return context;
};
